import type { ContainerLevelHistory } from "@ots-frontend/ots-data";
import { useMemo } from "react";
import { type OtsTimeSeries, OtsTimeSeriesChart, type OtsTimeSeriesMarker } from "../components/OtsTimeSeriesChart";
import { type ContainerCargo, containerCargoOf } from "./container-details-model";

export type ContainerDetailsChartProps = {
  levels: readonly ContainerLevelHistory[];
  /** The tank's capacity; the axis stops there so the line reads as how full it is. */
  capacity?: number;
  markers?: OtsTimeSeriesMarker[];
  height?: number;
};

const cargoes: { cargo: ContainerCargo; label: string; colorToken: string }[] = [
  { cargo: "water", label: "Water", colorToken: "--ots-transport-category-water" },
  { cargo: "oil", label: "Oil", colorToken: "--ots-transport-category-oil" },
];

const formatVolume = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * A tank's SCADA volume, one bare line per cargo in its cargo color, like the container profile's chart. Gaugings
 * are left out: the panel is for hauls against fill, and the readings table lives on the profile.
 */
export const ContainerDetailsChart = ({ levels, capacity, markers, height = 200 }: ContainerDetailsChartProps) => {
  const series = useMemo<OtsTimeSeries[]>(() => cargoes.map(({ cargo, label, colorToken }, slot) => ({
    id: cargo,
    label,
    slot,
    colorToken,
    style: "trace" as const,
    points: levels
      .filter((reading) => reading.type === "Meter" && reading.volume != null && containerCargoOf(reading.cargoSubtype) === cargo)
      .map((reading) => ({ time: reading.createDttm, value: reading.volume ?? 0 }))
      .sort((left, right) => left.time - right.time),
  })).filter((item) => item.points.length > 0), [levels]);
  const unit = levels.find((reading) => reading.volumeUom)?.volumeUom;

  if (!series.length) {
    return <p className="py-8 text-center text-xs text-[var(--ots-text-muted)]">No SCADA readings in this time.</p>;
  }
  return (
    <OtsTimeSeriesChart
      series={series}
      ariaLabel="Tank volume over time"
      valueLabel={`Volume${unit ? ` (${unit})` : ""}`}
      formatValue={formatVolume}
      markers={markers}
      max={capacity}
      height={height}/>
  );
};
